// src/tasks/services/use-care-model-queue.ts
// Task management R1: the care-model review steps (Checklist steps) that wait on a person, across the practice, shown as
// tasks with origin "Care model" in My tasks and the Team queue.
//
// There is no cross-patient "waiting checklists" read yet (PROD-3283 makes each item a Task in the store, after the
// conference). Until then this reads what the studio and the patient Overview read (#774), for every published care
// model of the organisation:
//   GET /v1/workflow-definitions (+ ?version=Published)           → every care model of the organisation, any publication state
//   GET /workflows/care-models/{definitionId}/enrolments          → each model's enrolments (paged); ACTIVE ones with a current step
//   readRun(enrolment) (#774)                                     → the Checklist step the run waits on, its items and "why"
//                                                                    (each definition version read once per queue read)
// Only runs that wait on a Checklist step are kept. Ticking reuses #774's `useChecklistTick` (the engine's checklist tick,
// which resumes the run). Read again every CARE_MODEL_POLL_MS (60 s) while the tab is visible: each refresh is several calls per active run.
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/auth/auth-store';
import { ApiError, getWorkflows } from '@/shared/api/api-client';
import { fetchDefinitionVersion } from '@/encounter/services/care-model-run-api';
import { ENROLMENTS_MAX_PAGES, ENROLMENTS_PAGE_SIZE } from '@/encounter/services/care-model-api';
import { isAutomationDefinition } from '@/encounter/lib/care-model-options';
import { careModelQueueKey, readRun, type DefinitionReader } from '@/patients/services/use-care-model-tasks';
import type { WaitingChecklist } from '@/patients/lib/care-model-tasks';
import type { PatientEnrolmentWire, PatientEnrolmentsResponse, WorkflowDefinitionPageWire } from '@/types/care-model-api';

export const CARE_MODEL_POLL_MS = 60_000;
export const RUN_READ_CONCURRENCY = 5;

export interface CareModelReview {
  enrolmentId: string;
  patientId: string;
  modelName: string | null;
  checklist: WaitingChecklist;
}

export interface CareModelQueue {
  reviews: CareModelReview[];
  /** Some care models or runs could not be read: the list may be incomplete, and says so. */
  incomplete: boolean;
}

type Active = PatientEnrolmentWire & { enrolmentId: string; patientId: string };

/**
 * The engine's answer for a listed definition it holds no care model for (Scrips.Workflows ListCareModelEnrolments:
 * 404 "No care model '{id}' was found."). Measured on dev 23 Oct 2026 for a definition the organisation's own list
 * returns: such a model can have no enrolments, so it reads as none. Any other 404 (a missing route, a gateway page),
 * a 401/403, a 5xx or a network error stays a failed read and the list says it may be incomplete.
 */
export function isNoCareModel(error: unknown, definitionId: string): boolean {
  if (!(error instanceof ApiError) || error.status !== 404) return false;
  try {
    const body: unknown = JSON.parse(error.body);
    const message = typeof body === 'object' && body !== null ? (body as { message?: unknown }).message : undefined;
    // The engine names the definition it could not find: it must be the one asked for.
    return typeof message === 'string' && message === `No care model '${definitionId}' was found.`;
  } catch {
    return false;
  }
}

/**
 * One definition read per version id for the whole queue read: many runs share a model version (dev, 23 Oct 2026:
 * 19 run reads named 6 versions). A version is read once and every run waits on that one answer (a failure is shared too,
 * and each of those runs says it could not be read). Scoped to one queue read, so a model saved later is read again.
 * Every caller must share one abort signal (one queue read has one): the first caller's signal drives the shared read.
 */
export function sharedDefinitionReader(read: DefinitionReader = fetchDefinitionVersion): DefinitionReader {
  const seen = new Map<string, Promise<unknown>>();
  return (id, signal) => {
    let answer = seen.get(id);
    if (!answer) {
      answer = read(id, signal);
      seen.set(id, answer);
    }
    return answer;
  };
}

/** The engine holds no care model for a listed definition (see isNoCareModel). */
class NoCareModel extends Error {}

async function modelEnrolments(definitionId: string, signal?: AbortSignal): Promise<PatientEnrolmentWire[]> {
  const all: PatientEnrolmentWire[] = [];
  for (let page = 1; page <= ENROLMENTS_MAX_PAGES; page++) {
    let res: PatientEnrolmentsResponse;
    try {
      res = await getWorkflows<PatientEnrolmentsResponse>(
        `/workflows/care-models/${encodeURIComponent(definitionId)}/enrolments?page=${page}&pageSize=${ENROLMENTS_PAGE_SIZE}`,
        { signal },
      );
    } catch (error) {
      // Only on the first page: a model that vanishes between pages is a failed (incomplete) read, never a shorter list.
      if (page === 1 && isNoCareModel(error, definitionId)) throw new NoCareModel();
      throw error;
    }
    if (!res || !Array.isArray(res.data)) throw new Error('enrolments: not the C4 envelope');
    all.push(...res.data);
    // A declared total binds: a list that ends before it is a failed (incomplete) read, never a shorter list.
    const total = typeof res.total === 'number' && Number.isFinite(res.total) && res.total >= 0 ? res.total : null;
    if (total !== null) {
      if (all.length >= total) return all;
      if (res.data.length < ENROLMENTS_PAGE_SIZE) throw new Error('enrolments: the list ended before its declared total');
    } else if (res.data.length < ENROLMENTS_PAGE_SIZE) return all;
  }
  throw new Error('enrolments: more pages than the reader accepts');
}

/** Run `fn` over `items`, at most `limit` at a time. */
async function pool<T, R>(items: readonly T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

export async function fetchCareModelQueue(signal?: AbortSignal): Promise<CareModelQueue> {
  // EVERY model the organisation has (latest versions) plus every published version: runs keep going under a newer draft
  // and after a model is retracted, so publication state never decides whose enrolments are read. Automations are left out.
  const [latest, published] = await Promise.all([
    getWorkflows<WorkflowDefinitionPageWire>('/v1/workflow-definitions', { signal }),
    getWorkflows<WorkflowDefinitionPageWire>('/v1/workflow-definitions?version=Published', { signal }),
  ]);
  if (!latest || !Array.isArray(latest.items) || !published || !Array.isArray(published.items))
    throw new Error('workflow-definitions: not the { items } page');
  const ids = [
    ...new Set(
      [...latest.items, ...published.items].filter((d) => !!d.definitionId && !isAutomationDefinition(d)).map((d) => d.definitionId as string),
    ),
  ];

  let incomplete = false;
  let notHeld = 0;
  const lists = await pool(ids, RUN_READ_CONCURRENCY, async (id) => {
    try {
      return await modelEnrolments(id, signal);
    } catch (error) {
      if (signal?.aborted) throw error;
      if (error instanceof NoCareModel) notHeld += 1;
      else incomplete = true;
      return [];
    }
  });
  // One definition the engine does not hold is a model with no runs. EVERY listed definition answering so is not an
  // empty practice: it is the engine not seeing this organisation (a tenant mismatch), so the list says it may be incomplete.
  if (ids.length > 0 && notHeld === ids.length) incomplete = true;
  const active = lists
    .flat()
    .filter((e): e is Active => !!e.enrolmentId && !!e.patientId && (e.status ?? '').toLowerCase() === 'active' && !!e.currentStep?.id);

  const readDefinition = sharedDefinitionReader();
  const runs = await pool(active, RUN_READ_CONCURRENCY, async (e) => ({ e, run: await readRun(e, signal, readDefinition) }));
  const reviews: CareModelReview[] = [];
  for (const { e, run } of runs) {
    if (run.unreadable) incomplete = true;
    if (run.checklist) reviews.push({ enrolmentId: e.enrolmentId, patientId: e.patientId, modelName: run.modelName, checklist: run.checklist });
  }
  return { reviews, incomplete };
}

export function useCareModelQueue(enabled = true) {
  const who = useAuthStore((s) => `${s.user?.organizationId ?? ''}|${s.user?.userId ?? ''}`);
  return useQuery<CareModelQueue>({
    // Prefixed by careModelQueueKey (a tick invalidates every user's), and keyed on who is signed in.
    queryKey: [...careModelQueueKey, who],
    enabled,
    queryFn: ({ signal }) => fetchCareModelQueue(signal),
    refetchInterval: CARE_MODEL_POLL_MS,
    // Each refresh re-reads every care model, enrolment and run: never while the tab is in the background.
    refetchIntervalInBackground: false,
  });
}

/** Mine: the step names me. Team: no one named (anyone in the practice may tick), someone else, or unreadable. */
export function isMyReview(r: CareModelReview, me: readonly (string | null | undefined)[]): boolean {
  const ids = r.checklist.assigneeIds;
  if (!ids || ids.length === 0) return false;
  const mine = new Set(me.filter((x): x is string => !!x).map((x) => x.trim().toLowerCase()));
  return ids.some((id) => mine.has(id.toLowerCase()));
}
